import React, { useCallback, useEffect, useMemo, useState } from 'react'
import UserLayout from '../../layouts/UserLayout'
import ButtonCreate from '../../components/common/ButtonCreate'
import InternalSaleForm from '../../components/InternalSaleForm'
import { apiBase } from '../../services/apiBase'
import { api } from '../../utils/api'
import { enqueueSnackbar } from 'notistack'
import PanelInternalOrderItem from '../../components/PanelInternalOrderItem'
import { Box, IconButton, Typography, TextField, MenuItem, Grid } from '@mui/material'
import { orderPanelStyles } from '../../components/common/orderPanelStyles'
import { useSelector } from 'react-redux'
import ContentCutIcon from '@mui/icons-material/ContentCut'
import DeleteIcon from '@mui/icons-material/Delete'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'

export default function InternalSale() {
	const currentStore = useSelector((s) => s.currentUser?.currentUser?.store)
	const [selectedOrder, setSelectedOrder] = useState(null)
	const [showForm, setShowForm] = useState(false)
	const [makerOrders, setMakerOrders] = useState([])
	const [checkerOrders, setCheckerOrders] = useState([])
	const { currentUser } = useSelector((state) => state.currentUser)
	const [fromDate, setFromDate] = useState(dayjs())
	const [toDate, setToDate] = useState(dayjs())
	const [orderType, setOrderType] = useState('')
	const [status, setStatus] = useState('')

	const internalSaleOrders = useMemo(() => {
		return [...makerOrders, ...checkerOrders].sort((a, b) => {
			return new Date(b.createAt) - new Date(a.createAt)
		})
	}, [makerOrders, checkerOrders])

	const fetchMakerOrders = useCallback(async () => {
		try {
			const res = await apiBase.get(api.GET_INTERNAL_SALE_ORDERS, {
				fromDate: fromDate?.startOf('day').toISOString(),
				toDate: toDate?.endOf('day').toISOString(),
				status: status === 'all' || status === '' ? undefined : status,
				orderType: orderType === 'all' || orderType === '' ? undefined : orderType,
			})
			if (Array.isArray(res)) {
				setMakerOrders(res)
			}
		} catch (error) {
			enqueueSnackbar(error?.response?.data?.message, { variant: 'error' })
		}
	}, [fromDate, toDate, orderType, status])

	const fetchCheckerOrders = useCallback(async () => {
		try {
			const res = await apiBase.get(api.GET_INTERNAL_SALE_ORDER_CHECKER_ACCEPT, {
				fromDate: fromDate?.startOf('day').toISOString(),
				toDate: toDate?.endOf('day').toISOString(),
				status: status === 'all' || status === '' ? undefined : status,
				orderType: orderType === 'all' || orderType === '' ? undefined : orderType === 'BUY' ? 'SELL' : 'BUY',
			})
			if (Array.isArray(res)) {
				setCheckerOrders(res)
			}
		} catch (error) {
			enqueueSnackbar(error?.response?.data?.message, { variant: 'error' })
		}
	}, [fromDate, toDate, orderType, status])

	useEffect(() => {
		fetchMakerOrders()
		fetchCheckerOrders()
	}, [fetchMakerOrders, fetchCheckerOrders])

	const deleteOrder = useCallback(
		async (orderId) => {
			try {
				await apiBase.delete(api.DELETE_INTERNAL_SALE_ORDER, { orderId: orderId })
				enqueueSnackbar('Xóa đơn hàng thành công!', { variant: 'success' })
				fetchMakerOrders()
			} catch (error) {
				enqueueSnackbar(error?.response?.data?.message, { variant: 'error' })
			}
		},
		[fetchMakerOrders],
	)

	return (
		<UserLayout>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, mb: 1 }}>
					<Grid container spacing={1} alignItems='center'>
						<Grid item xs={3}>
							<DatePicker
								label='Từ ngày'
								defaultValue={fromDate}
								onAccept={(newValue) => setFromDate(newValue)}
								renderInput={(params) => <TextField {...params} />}
								slotProps={{ textField: { sx: { width: '150px' }, size: 'small' } }}
								maxDate={dayjs()}
							/>
						</Grid>
						<Grid item xs={3}>
							<DatePicker
								label='Đến ngày'
								defaultValue={toDate}
								onAccept={(newValue) => setToDate(newValue)}
								renderInput={(params) => <TextField {...params} />}
								slotProps={{ textField: { sx: { width: '150px' }, size: 'small' } }}
								maxDate={dayjs()}
							/>
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<TextField
								select
								label='Loại đơn hàng'
								value={orderType}
								onChange={(e) => setOrderType(e.target.value)}
								sx={{ width: '150px' }}
								size='small'>
								<MenuItem value='all'>Tất cả</MenuItem>
								<MenuItem value='SELL'>Đơn bán</MenuItem>
								<MenuItem value='BUY'>Đơn mua</MenuItem>
							</TextField>
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<TextField
								select
								label='Trạng thái'
								value={status}
								onChange={(e) => setStatus(e.target.value)}
								sx={{ width: '150px' }}
								size='small'>
								<MenuItem value='all'>Tất cả</MenuItem>
								<MenuItem value='pending'>Chờ xử lý</MenuItem>
								<MenuItem value='accept'>Chấp nhận</MenuItem>
								<MenuItem value='reject'>Từ chối</MenuItem>
							</TextField>
						</Grid>
					</Grid>
				</Box>
			</LocalizationProvider>

			<Box sx={{ ...orderPanelStyles.container, mb: 10, mt: 1 }}>
				<Box sx={orderPanelStyles.ordersBox}>
					{internalSaleOrders.map((order) => (
						<PanelInternalOrderItem
							key={order._id}
							order={order}
							renderActions={() =>
								order?.fromStore?._id === currentStore ? (
									<Box sx={orderPanelStyles.actionIcons}>
										{currentUser?.permission?.includes('updateOrder') && order?.status === 'pending' && (
											<IconButton
												size='small'
												sx={{ color: '#1976d2' }}
												onClick={(e) => {
													e.stopPropagation()
													setSelectedOrder(order)
													setShowForm(true)
												}}>
												<ContentCutIcon sx={{ color: '#9C27B0', cursor: 'pointer' }} fontSize='small' />
											</IconButton>
										)}

										{currentUser?.permission?.includes('deleteOrder') && order?.status === 'pending' && (
											<IconButton
												size='small'
												sx={{ color: 'purple' }}
												onClick={(e) => {
													e.stopPropagation()
													const confirmDelete = window.confirm('Bạn có chắc muốn xóa đơn hàng này?')
													if (confirmDelete) {
														deleteOrder(order._id)
													}
												}}>
												<DeleteIcon sx={{ color: '#f44336', cursor: 'pointer' }} fontSize='small' />
											</IconButton>
										)}
									</Box>
								) : null
							}
						/>
					))}

					{internalSaleOrders.length === 0 ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
							<Typography variant='h6'>Không có đơn hàng</Typography>
						</Box>
					) : null}
				</Box>
			</Box>

			<ButtonCreate onClick={() => setShowForm(true)} />
			<InternalSaleForm
				initOrder={selectedOrder}
				closeForm={(success) => {
					setShowForm(false)
					setSelectedOrder(null)
					if (success) {
						fetchMakerOrders()
					}
				}}
				open={showForm}
			/>
		</UserLayout>
	)
}

